import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Activity } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAssetHealthOverview } from '@/hooks/use-asset-management-widgets';
import { Skeleton } from '@/components/ui/skeleton';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

// Use the same color scheme as Warranty Status widget
// Colors for health conditions and asset status types
const HEALTH_COLORS: Record<string, string> = {
  'excellent': '#10b981', // green-500
  'good': '#3b82f6', // blue-500
  'fair': '#f59e0b', // amber-500
  'poor': '#f97316', // orange-500
  'critical': '#ef4444', // red-500
  // Current asset status types
  'inservice': '#10b981', // green-500 - In Service
  'outofservice': '#94a3b8', // slate-400 - Out of Service
  'disposed': '#dc2626', // red-600 - Disposed
  'fullydepreciated': '#8b5cf6', // violet-500 - Fully Depreciated
  'pending': '#a855f7', // purple-500 - Pending / On Order
  'undermaintenance': '#f59e0b', // amber-500 - Under Maintenance
  'reserved': '#06b6d4', // cyan-500 - Reserved
  'lostmissing': '#ef4444', // red-500 - Lost / Missing
  'leased': '#3b82f6', // blue-500 - Leased
  // Legacy status types for backwards compatibility
  'inactive': '#94a3b8', // slate-400
  'active': '#10b981', // green-500
  'maintenance': '#f59e0b', // amber-500
  'retired': '#64748b', // slate-500
  'unknown': '#6b7280', // gray-500
};

// Helper function to convert status to title case
const toTitleCase = (str: string): string => {
  return str
    .split(/[\s_-]+/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

interface CustomTooltipProps {
  readonly active?: boolean;
  readonly payload?: Array<{ payload: { name: string; value: number; percentage: number } }>;
}

const CustomTooltipComponent = ({ active, payload }: CustomTooltipProps) => {
  if (active && payload?.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow-lg border">
        <p className="font-semibold">{data.name}</p>
        <p className="text-sm text-muted-foreground">
          {data.value} assets ({data.percentage.toFixed(1)}%)
        </p>
      </div>
    );
  }
  return null;
};

interface AssetHealthWidgetProps {
  readonly className?: string;
}

export function AssetHealthWidget({ className }: Readonly<AssetHealthWidgetProps>): React.ReactElement {
  const { data, isLoading, error } = useAssetHealthOverview();

  if (isLoading) {
    return (
      <Card className={cn('relative overflow-hidden shadow-sm bg-white dark:bg-black', className)}>
        <CardHeader>
          <CardTitle className="text-base font-semibold tracking-tight">Asset Health Overview</CardTitle>
          <CardDescription>Overall asset health status</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <Skeleton className="h-64 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card className={cn('relative overflow-hidden shadow-sm bg-white dark:bg-black', className)}>
        <CardHeader>
          <CardTitle className="text-base font-semibold tracking-tight">Asset Health Overview</CardTitle>
          <CardDescription>Overall asset health status</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-muted-foreground">No health data available.</div>
        </CardContent>
      </Card>
    );
  }

  const chartData = (data.statusBreakdown || []).map(item => ({
    name: toTitleCase(item.status),
    value: item.count,
    percentage: item.percentage,
    color: item.color || HEALTH_COLORS[item.status.toLowerCase()] || HEALTH_COLORS['unknown'],
  })).filter(item => item.value > 0);

  return (
    <Card className={cn('relative overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 bg-white dark:bg-black h-full flex flex-col', className)}>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="space-y-1.5">
            <CardTitle className="text-base font-semibold tracking-tight">Asset Health Overview</CardTitle>
            <CardDescription>Overall asset health status</CardDescription>
          </div>
          <div className="p-2 bg-blue-50 dark:bg-blue-950 rounded-lg">
            <Activity className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex-1">
        {/* Single Column Layout - Chart, Legend, and Total */}
        <div className="space-y-4">
          {/* Chart */}
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={75}
                paddingAngle={2}
                dataKey="value"
              >
                {chartData.map((entry) => (
                  <Cell key={entry.name} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltipComponent />} />
            </PieChart>
          </ResponsiveContainer>

          {/* Legend */}
          <div className="grid grid-cols-2 gap-2">
            {chartData.map((item) => (
              <div key={item.name} className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: item.color }}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium truncate">{item.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {item.value} ({item.percentage.toFixed(1)}%)
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Total */}
          <div className="pt-2 border-t">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Total Assets</span>
              <span className="text-lg font-semibold">{data.totalAssets}</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
